import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import type { ApiError } from '@/core/api';
import * as api from './api';

export const tagKeys = {
  all: ['tags'] as const,
  vocabulary: () => [...tagKeys.all, 'vocabulary'] as const,
  contentType: (modelLabel: string) => [...tagKeys.all, 'ct', modelLabel] as const,
  forEntity: (modelLabel: string, objectId: string | number) =>
    [...tagKeys.all, 'entity', modelLabel, String(objectId)] as const,
};

/** Resolve "app_label.model" → ContentType id. Cached forever (never changes). */
export function useContentTypeId(modelLabel: string) {
  return useQuery({
    queryKey: tagKeys.contentType(modelLabel),
    queryFn: () => api.resolveContentType(modelLabel),
    enabled: Boolean(modelLabel),
    staleTime: Infinity,
  });
}

/** The org's full tag vocabulary — drives the picker and name/color lookup. */
export function useTagVocabulary() {
  return useQuery({
    queryKey: tagKeys.vocabulary(),
    queryFn: api.listTags,
    staleTime: 60_000,
  });
}

/** Tags currently linked to one entity, joined to the vocabulary for display. */
export function useEntityTags(modelLabel: string, objectId: string | number) {
  const ct = useContentTypeId(modelLabel);
  const ctId = ct.data?.id;

  const links = useQuery({
    queryKey: tagKeys.forEntity(modelLabel, objectId),
    queryFn: () => api.listTaggedItems({ content_type: ctId!, object_id: String(objectId) }),
    enabled: Boolean(ctId && objectId),
  });

  return {
    contentTypeId: ctId,
    links,
    isPending: ct.isPending || (Boolean(ctId) && links.isPending),
    isError: ct.isError || links.isError,
    error: ct.error ?? links.error,
  };
}

export function useAddTag(modelLabel: string, objectId: string | number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { tag: number; content_type: number }) =>
      api.createTaggedItem({ ...input, object_id: String(objectId) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: tagKeys.forEntity(modelLabel, objectId) }),
    onError: (err: ApiError) => toast.error(err.message),
  });
}

export function useRemoveTag(modelLabel: string, objectId: string | number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (taggedItemId: number) => api.deleteTaggedItem(taggedItemId),
    onSuccess: () => qc.invalidateQueries({ queryKey: tagKeys.forEntity(modelLabel, objectId) }),
    onError: (err: ApiError) => toast.error(err.message),
  });
}

/** Create a brand-new tag in the org vocabulary, then refresh the picker. */
export function useCreateTag() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { name: string; color?: string }) => api.createTag(input),
    onSuccess: () => qc.invalidateQueries({ queryKey: tagKeys.vocabulary() }),
    onError: (err: ApiError) => {
      if (!err.fieldErrors) toast.error(err.message);
    },
  });
}
